/**
 * OneShot GL by Cosmo Myzrail Gorynych aka Comigo Games
 * All rights reserved, at least for now
 * Do not plagiarize/copy/redistribute
 */

const glsl = e => e;

const vectorShader = '' + glsl`
attribute vec2 a_position;
attribute vec2 a_texCoord;

uniform vec2 u_resolution;

varying vec2 v_texCoord;

void main() {
   // convert the rectangle from pixels to 0.0 to 1.0
   vec2 zeroToOne = a_position / u_resolution;

   // convert from 0->1 to 0->2
   vec2 zeroToTwo = zeroToOne * 2.0;

   // convert from 0->2 to -1->+1 (clipspace)
   vec2 clipSpace = zeroToTwo - 1.0;

   gl_Position = vec4(clipSpace * vec2(1, -1), 0, 1);

   // pass the texCoord to the fragment shader
   // The GPU will interpolate this value between points.
   v_texCoord = a_texCoord;
}
`
const fragmentTemplate = '' + glsl`
    precision mediump float;
    // our texture
    uniform sampler2D u_image;
    uniform vec2 u_textureSize;
    // the texCoords passed in from the vertex shader.
    varying vec2 v_texCoord;
    \${uniforms}
    \${functions}
    void main() {
       vec2 pixel = vec2(1.0, 1.0) / u_textureSize;
       // Look up a color from the texture.
       \${source}
    }
`;

const VERT = WebGLRenderingContext.VERTEX_SHADER,
      FRAG = WebGLRenderingContext.FRAGMENT_SHADER;

const getRGB = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
    ];
};

class Renderer {
    canvas = document.createElement('canvas');
    private gl: WebGLRenderingContext;
    private vecShader: WebGLShader;
    private fragShader: WebGLShader;
    private program: WebGLProgram;
    private texture: WebGLTexture;
    private paramNotation: object;
    constructor(fragCode: string, params?: object, functions?: string) {
        if (!functions && typeof params === 'string') {
            functions = params;
            params = {};
        }
        this.paramNotation = params || {};
        const gl = this.gl;
        this.canvas.addEventListener('webglcontextlost', e => {
            e.preventDefault();
        }, false);
        this.canvas.addEventListener('webglcontextrestored', this.setup.bind(this, [fragCode, this.paramNotation]), false);
        this.canvas.width = this.canvas.height = 1;
        this.setup(fragCode, this.paramNotation, functions);
        return this;
    }
    setup(fragCode: string, params?: object, functions?: string) {
        this.gl = this.canvas.getContext('webgl2', {
            premultipliedAlpha: false
        });
        const gl = this.gl;
        this.vecShader = gl.createShader(VERT);
        this.fragShader = gl.createShader(FRAG);
        this.program = gl.createProgram();

        // Create a texture.
        this.texture = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, this.texture);

        // Set the parameters so we can render any size image.
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);

        var fragSource = fragmentTemplate;
        // tslint:disable-next-line: no-invalid-template-strings
        fragSource = fragSource.replace('${source}', fragCode).replace('${functions}', functions || '');
        var uniformsCode = '';
        params = params || {};
        for (const key in params) {
            if (params[key] === 'number' || params[key] === 'float') {
                uniformsCode += `uniform float ${key};\n`;
            } else if (params[key] === 'color' || params[key] === 'vec3') {
                uniformsCode += `uniform vec3 ${key};\n`;
            } else if (params[key] === 'bool' || params[key] === 'boolean') {
                uniformsCode += `uniform bool ${key};\n`;
            } else if (params[key] === 'vec2') {
                uniformsCode += `uniform vec2 ${key};\n`;
            } else if (params[key] === 'vec4') {
                uniformsCode += `uniform vec4 ${key};\n`;
            } else if (params[key].indexOf('int') !== -1) {
                uniformsCode += `uniform ${params[key]} ${key};\n`;
            }
        }
        // tslint:disable-next-line: no-invalid-template-strings
        fragSource = fragSource.replace('${uniforms}', uniformsCode);
        gl.shaderSource(this.fragShader, fragSource);
        gl.shaderSource(this.vecShader, vectorShader);
        gl.compileShader(this.fragShader);
        gl.compileShader(this.vecShader);
        if (!gl.getShaderParameter(this.fragShader, gl.COMPILE_STATUS)) {
            console.error(gl.getShaderInfoLog(this.fragShader));
            console.error(fragSource);
            return null;
        }
        if (!gl.getShaderParameter(this.vecShader, gl.COMPILE_STATUS)) {
            console.error(gl.getShaderInfoLog(this.vecShader));
            console.error(fragSource);
            return null;
        }
        gl.attachShader(this.program, this.vecShader);
        gl.attachShader(this.program, this.fragShader);
        gl.linkProgram(this.program);
        if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
            console.error(gl.getProgramInfoLog(this.program));
            gl.deleteProgram(this.program);
            return null;
        }
    }
    render(image: HTMLImageElement | HTMLCanvasElement, params?: object, positions?: Array<number>): HTMLCanvasElement {
        if (this.canvas.width !== image.width ||
            this.canvas.height !== image.height
        ) {
            this.canvas.width = image.width;
            this.canvas.height = image.height;
        }
        params = params || {};
        const gl = this.gl;
        const pos = new Float32Array(positions || [
            0, 0,
            image.width, 0,
            0, image.height,
            0, image.height,
            image.width, 0,
            image.width, image.height
        ]);

        const pal = gl.getAttribLocation(this.program, 'a_position');
        // look up where the texture coordinates need to go.
        const texCoordLocation = gl.getAttribLocation(this.program, 'a_texCoord');

        const pBuf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, pBuf);
        gl.bufferData(gl.ARRAY_BUFFER, pos, gl.STATIC_DRAW);

        // provide texture coordinates for the rectangle.
        const texCoordBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
            0.0, 0.0,
            1.0, 0.0,
            0.0, 1.0,
            0.0, 1.0,
            1.0, 0.0,
            1.0, 1.0
        ]), gl.STATIC_DRAW);

        // Upload the image into the texture.
        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);

        // lookup uniforms
        const resolutionLocation = gl.getUniformLocation(this.program, 'u_resolution');
        const textureSizeLocation = gl.getUniformLocation(this.program, 'u_textureSize');

        // Resize the drawing buffer to fit the source image
        gl.viewport(0, 0, image.width, image.height);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);

        gl.useProgram(this.program);

        gl.enableVertexAttribArray(pal);
        gl.bindBuffer(gl.ARRAY_BUFFER, pBuf);
        gl.vertexAttribPointer(pal, 2, gl.FLOAT, false, 0, 0);

        gl.enableVertexAttribArray(texCoordLocation);
        gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
        gl.vertexAttribPointer(texCoordLocation, 2, gl.FLOAT, false, 0, 0);

        gl.uniform2f(resolutionLocation, image.width, image.height);
        gl.uniform2f(textureSizeLocation, image.width, image.height);

        for (const key in params) {
            if (Object.hasOwnProperty.apply(params, [key])) {
                const param = params[key];
                const loc = gl.getUniformLocation(this.program, key);
                if (typeof param === 'number') {
                    if (this.paramNotation[key] === 'int') {
                        gl.uniform1i(loc, param);
                    } else {
                        gl.uniform1f(loc, param);
                    }
                } else if (typeof param === 'string' && param[0] === '#') { // a hex-encoded color
                    const [r, g, b] = getRGB(param);
                    gl.uniform3f(loc, r / 256, g / 256, b / 256);
                } else if (typeof param === 'boolean') {
                    gl.uniform1f(loc, param ? 1 : 0);
                } else if (param && typeof param[Symbol.iterator] === 'function') { // an array or such
                    if (param.length === 1) {
                        gl.uniform1f(loc, param[0]);
                    } else if (param.length === 2) {
                        gl.uniform2f(loc, param[0], param[1]);
                    } else if (param.length === 3) {
                        gl.uniform3f(loc, param[0], param[1], param[2]);
                    } else if (param.length === 4) {
                        gl.uniform4f(loc, param[0], param[1], param[2], param[3]);
                    }
                }
            }
        }

        gl.finish();

        gl.drawArrays(gl.TRIANGLES, 0, 6);
        gl.finish();

        return this.canvas;
    }
}

export {Renderer};
